import { Input, Button, Table } from 'reactstrap';
import type { Attribute } from '../../api/itemSchemas';
import { VpSelect } from '../ui/VpSelect';

export interface AttributesGridProps {
  value: Attribute[];
  onChange: (next: Attribute[]) => void;
  readOnly?: boolean;
}

const TYPES: Attribute['type'][] = ['text', 'int', 'enum', 'date', 'bool'];
const ROLES: Attribute['voice_role'][] = ['name', 'discriminator', 'info', 'none'];
const ROLE_LABELS: Record<Attribute['voice_role'], string> = {
  name: 'Name',
  discriminator: 'Discriminator',
  info: 'Info',
  none: 'None',
};

function newRow(): Attribute {
  return { name: '', type: 'text', required: false, voice_role: 'none', voice_speak_template: null };
}

export function AttributesGrid({ value, onChange, readOnly = false }: AttributesGridProps) {
  const update = (i: number, patch: Partial<Attribute>) => {
    if (readOnly) return;
    onChange(value.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  };
  const remove = (i: number) => { if (!readOnly) onChange(value.filter((_, idx) => idx !== i)); };
  const add = () => { if (!readOnly) onChange([...value, newRow()]); };
  const move = (i: number, dir: -1 | 1) => {
    if (readOnly) return;
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const names = value.map((v) => v.name);
  const dup = names.length !== new Set(names).size;

  return (
    <div>
      {dup && (
        <div className="alert alert-danger py-2 px-3 small mb-2">
          I nomi attributi devono essere unici.
        </div>
      )}
      <div className="table-responsive">
      <Table className="table-sm align-middle table-striped mb-0" role="grid">
        <thead>
          <tr>
            <th style={{ width: 24 }}></th>
            <th>Name</th>
            <th>Type</th>
            <th>Req</th>
            <th>Voice role</th>
            <th>Speak template</th>
            <th style={{ width: 96 }}></th>
          </tr>
        </thead>
        <tbody>
          {value.map((row, i) => (
            <tr key={i}>
              <td style={{ color: 'var(--vp-text-3)', cursor: readOnly ? 'default' : 'grab' }}>⋮⋮</td>
              <td>
                <Input value={row.name} onChange={(e) => update(i, { name: e.target.value })} aria-label={`name riga ${i + 1}`} disabled={readOnly} />
              </td>
              <td>
                <VpSelect ariaLabel={`type riga ${i + 1}`} isDisabled={readOnly}
                  options={TYPES.map((t) => ({ value: t, label: t }))}
                  value={row.type}
                  onChange={(v) => update(i, { type: v as Attribute['type'] })} />
              </td>
              <td>
                <Input type="checkbox" checked={row.required} onChange={(e) => update(i, { required: e.target.checked })} aria-label={`required riga ${i + 1}`} disabled={readOnly} />
              </td>
              <td>
                <VpSelect ariaLabel={`voice_role riga ${i + 1}`} isDisabled={readOnly}
                  options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
                  value={row.voice_role}
                  onChange={(v) => update(i, { voice_role: v as Attribute['voice_role'] })} />
              </td>
              <td>
                <Input value={row.voice_speak_template ?? ''} onChange={(e) => update(i, { voice_speak_template: e.target.value || null })} placeholder="—" aria-label={`speak_template riga ${i + 1}`} disabled={readOnly} />
              </td>
              <td>
                <Button type="button" color="light" size="sm" onClick={() => move(i, -1)} aria-label={`sposta su riga ${i + 1}`} disabled={readOnly || i === 0}>↑</Button>
                <Button type="button" color="light" size="sm" onClick={() => move(i, 1)} aria-label={`sposta giù riga ${i + 1}`} disabled={readOnly || i === value.length - 1}>↓</Button>
                <Button type="button" color="light" size="sm" onClick={() => remove(i)} aria-label={`rimuovi riga ${i + 1}`} disabled={readOnly}>×</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
      <Button type="button" color="secondary" outline size="sm" onClick={add} className="mt-2" disabled={readOnly}>+ aggiungi attributo</Button>
    </div>
  );
}
